/**
 * Обложка поста: фон из набора бренда, дата запланированного слота, метка канала и крючок —
 * строка, ради которой пост открывают. docs/phase-2/10-posters.md.
 *
 * ЭТО ДРУГОЙ ПРОДУКТ, ЧЕМ КАРТОЧКА ИЗ `visual.ts`, и поэтому другой модуль. Карточка —
 * инфографика: она обязана заменить строку поста данными (М1–М6, ВИЗ-1…ВИЗ-12), выносится
 * редко и приезжает предложением. Обложка — лицо поста в ленте: она есть у КАЖДОГО поста
 * бренда, у которого включена (решение Ильи 2026-10-28), и едет внутри версии, а не рядом
 * с ней: утверждение поста утверждает и обложку. Смешать их в одно объединение значило бы,
 * что вердикт инфографики начал бы предлагать модели «обложку», а гейт обложки — требовать
 * от крючка дословности, которой у крючка не бывает.
 *
 * ЧТО ОБЛОЖКА НЕ ПРИДУМЫВАЕТ НИКОГДА. Дату и метку ставит код, а не модель: дата — день
 * запланированного слота (`plannedPosterDay`), метка — `visual.poster.tag` конфига бренда.
 * Крючок пишет модель, но цифры и имена в нём обязаны встречаться в посте (гейт писателя),
 * то есть картинка по-прежнему не утверждает факта, которого не утверждает пост.
 *
 * ПОЧЕМУ СПИСКИ ЗАКРЫТЫ. Формат крючка и настроение — перечисления, а не свободные строки:
 * по формату код чередует крючки (`posterExclusions`), и свободная строка «вопрос» против
 * «вопрос-интрига» сделала бы чередование словарём синонимов. Новый формат — правка этого
 * списка и файла базы знаний, а не находка модели посреди прогона.
 */

import * as z from 'zod';
import { UUID_V7_RE } from '../ids.ts';
import { dayKeyOf, ISO_UTC_MS, isDayKey, isoWeekdayOfDayKey, wallMinuteToMs } from '../time.ts';
import { codePointLen } from '../units.ts';
import { isVisualText, VISUAL_REASON_MAX, VISUAL_WIDTH } from './visual.ts';

/* ─────────────────────────── словари ─────────────────────────── */

/**
 * Форматы крючка. Пять названы Ильёй в задании (риторический вопрос, вопрос с многоточием,
 * фраза, коротко переписанный заголовок, тезис), семь добавлены под настроения, которым те
 * пять не подходят: серьёзной новости — следствие и сухая констатация, практической — срок и
 * ситуация читателя, новости с цифрой — число. Что значит каждый и когда берётся —
 * `knowledge/publishing/poster.md`; здесь только имена, по которым код чередует.
 *
 * ЗНАЧЕНИЯ НЕ УДАЛЯЮТСЯ — ни здесь, ни в `POSTER_MOODS`. Спецификации в базе записаны прежним
 * списком, и модель рендера с видом черновика читают их строго: снятый формат превратил бы
 * обложку старой версии в «картинку без обложки» — без полосы обложки и без подсказки дня в
 * диалоге (гейт дня и история чередования читают терпимо и не пострадают). Перестать
 * предлагать формат — значит убрать его из инструкции и из `allowedPosterFormats`.
 */
export const POSTER_HOOK_FORMATS = [
  'question',
  'open_question',
  'phrase',
  'headline',
  'thesis',
  'consequence',
  'number',
  'stakes',
  'deadline',
  'deadpan',
  'reader',
  'juxtaposition',
] as const;
export const PosterHookFormatSchema = z.enum(POSTER_HOOK_FORMATS);
export type PosterHookFormat = z.output<typeof PosterHookFormatSchema>;

/**
 * Общее настроение поста — первое, что дизайнер называет, ДО выбора формата. Шкалы ниже
 * говорят «насколько», настроение — «какое»: тревожная новость высокой важности и
 * любопытная новость той же важности требуют разных крючков.
 */
export const POSTER_MOODS = [
  'neutral',
  'serious',
  'alarming',
  'optimistic',
  'curious',
  'ironic',
] as const;
export const PosterMoodSchema = z.enum(POSTER_MOODS);
export type PosterMood = z.output<typeof PosterMoodSchema>;

/* ─────────────────────────── размеры ─────────────────────────── */

/**
 * Крючок в кодпоинтах. Верх — не стиль, а вёрстка: три строки самым мелким кеглем лестницы
 * (`poster-template.ts`) на ширине текстовой зоны. Влезет ли конкретная строка, решает
 * измерение, а не это число; число только не пускает абзац туда, где ждут строку.
 */
export const POSTER_HOOK_MIN = 4;
export const POSTER_HOOK_MAX = 80;
/** Краткий пересказ прочтения поста — одна строка для полосы визуала, не абзац. */
export const POSTER_SUMMARY_MIN = 10;
export const POSTER_SUMMARY_MAX = 200;
/** Почему этот формат и этот фон — одна строка для полосы визуала. */
export const POSTER_WHY_MAX = 300;

/**
 * Сколько последних обложек не дают повторить свой формат крючка. Два — «ни один формат
 * два раза подряд и не через один»: при двенадцати форматах это убирает повтор поверхности
 * в соседних постах (docs/instruction-writing.md ИНС-7) и оставляет дизайнеру десять.
 */
export const POSTER_FORMAT_GAP = 2;

/**
 * Меньше скольких фонов на выбор чередование ослабляется (`allowedPosterBackgrounds`).
 * Фон без выбора — это не дизайнер, а очередь, поэтому три, а не один.
 */
export const POSTER_MIN_BACKGROUND_CHOICE = 3;

/**
 * Меньше скольких фонов «не повторять фон соседа» невыполнимо по построению: столько обязан
 * нести набор (`PosterManifestSchema`) и столько обязана оставить поверхность бренда
 * (`visual.poster.surfaces`, проверка на старте писателя).
 */
export const POSTER_MIN_USABLE_BACKGROUNDS = 2;

/**
 * Запас между созданием черновика и слотом, на дату которого он рисуется. Пост, написанный
 * в 16:30, к слоту 17:00 не успевает пройти согласование, и рисовать на нём сегодняшнюю дату
 * значило бы гарантированную перерисовку. Два часа, а не сутки `approval_sla_hours`: сутки
 * отправили бы пятничный дайджест, собранный в 10:00, на вторник, хотя пятничный слот 17:00
 * заведён ровно под него.
 */
export const POSTER_SLOT_LEAD_MS = 2 * 60 * 60 * 1000;

/* ─────────────────────────── строки ─────────────────────────── */

const TAG_RE = /^@[A-Za-z][A-Za-z0-9_]{4,31}$/;
const SET_RE = /^[a-z0-9][a-z0-9-]{1,31}$/;
const BACKGROUND_RE = /^[a-z0-9][a-z0-9-]{1,31}$/;

/**
 * Метка канала — `@username` боевого канала по правилам Telegram: 5–32 знака, латиница,
 * цифры и подчёркивание, первая — буква. Это ПОДПИСЬ на картинке, а не привязка канала:
 * привязку держит `channels.telegram.channel_ref` у оператора на сервере.
 */
export const PosterTagSchema = z.string().regex(TAG_RE, 'ожидалась метка вида @channel_name');
/** Набор фонов — каталог `assets/posters/<set>/` с `manifest.json`. */
export const PosterSetSchema = z.string().regex(SET_RE, 'ожидалось имя набора фонов');
export const PosterBackgroundIdSchema = z.string().regex(BACKGROUND_RE, 'ожидался id фона');

function cpLen(s: string): number {
  return codePointLen(s) as unknown as number;
}

/** Календарный день `YYYY-MM-DD`, который на самом деле существует (не 2026-02-30). */
export function isPosterDay(s: string): boolean {
  return isDayKey(s);
}

export const PosterDaySchema = z
  .string()
  .refine(isPosterDay, { error: 'ожидалась календарная дата YYYY-MM-DD' });

/**
 * Крючок. Алфавит — тот же, что у карточки (`VISUAL_CHARSET_RE`): кодпоинт вне гарнитуры
 * рисуется прямоугольником .notdef, и ни растеризатор, ни тест байтов об этом не скажут.
 * Пробелы по краям и двойные пробелы отбиваются, а не чистятся: перенос строк считается по
 * пробелам, и лишний пробел — это лишняя пустая «строка» в измерении.
 */
export const PosterHookSchema = z
  .string()
  .min(POSTER_HOOK_MIN)
  .max(POSTER_HOOK_MAX)
  .refine((s) => cpLen(s) >= POSTER_HOOK_MIN && cpLen(s) <= POSTER_HOOK_MAX, {
    error: `ожидалось ${String(POSTER_HOOK_MIN)}..${String(POSTER_HOOK_MAX)} кодпоинтов`,
  })
  .refine(isVisualText, { error: 'символ вне алфавита картинки' })
  .refine((s) => s === s.trim() && !s.includes('  '), {
    error: 'лишние пробелы в крючке',
  });

/** Слова крючка — то, из чего выбирается акцент и по чему считается перенос. */
export function posterHookWords(hook: string): readonly string[] {
  return hook.split(' ').filter((w) => w !== '');
}

/**
 * Акцентное слово — одно слово крючка, которое вёрстка красит акцентом бренда («один
 * акцент словом», правила дизайна GeoBoost v3.7). Сравнение — по слову без краевой
 * пунктуации: модель вправе назвать «картинка», когда в крючке стоит «картинка?».
 */
export function posterAccentIndex(hook: string, accent: string): number {
  if (accent === '') return -1;
  const bare = (w: string): string => w.replace(/^[«"„(]+|[»"“”).,:;!?…]+$/gu, '');
  return posterHookWords(hook).findIndex((w) => bare(w) === accent || w === accent);
}

/* ─────────────────────────── прочтение и спецификация ─────────────────────────── */

const Scale = z.int().min(1).max(5);

/**
 * Прочтение поста — то, что дизайнер обязан назвать ДО крючка (задание Ильи: «перед
 * написанием текста модель должна ознакомиться с сентиментикой поста»). Хранится в
 * спецификации не для красоты: полоса визуала показывает его человеку, и «почему вопрос, а
 * не тезис» читается по нему, а не угадывается.
 */
export const PosterReadSchema = z.strictObject({
  mood: PosterMoodSchema,
  /** Насколько это важно читателю канала. */
  importance: Scale,
  /** Насколько это неожиданно или тревожно. */
  shock: Scale,
  /** Насколько в поводе есть место улыбке. */
  humor: Scale,
  /** Насколько тема требует сдержанности. */
  seriousness: Scale,
  /** Насколько из поста следует действие читателя. */
  practical: Scale,
  summary: z
    .string()
    .min(POSTER_SUMMARY_MIN)
    .max(POSTER_SUMMARY_MAX)
    .refine((s) => cpLen(s) >= POSTER_SUMMARY_MIN && cpLen(s) <= POSTER_SUMMARY_MAX, {
      error: `ожидалось ${String(POSTER_SUMMARY_MIN)}..${String(POSTER_SUMMARY_MAX)} кодпоинтов`,
    }),
});
export type PosterRead = z.output<typeof PosterReadSchema>;

/**
 * Спецификация обложки — всё, из чего картинка собирается заново байт в байт. Поэтому в ней
 * есть и то, что выбрал код (`set`, `date`, `tag`): перерисовка даты (`POSTER_REDATE_PREFIX`)
 * меняет ровно `date` и рендерит остальное как было, без вызова модели.
 *
 * Лежит в `media_asset.spec_json` рядом с байтами — оттуда её читают проверка даты при
 * одобрении, чередование фонов и полоса визуала.
 */
export const PosterSpecSchema = z
  .strictObject({
    kind: z.literal('poster'),
    set: PosterSetSchema,
    background: PosterBackgroundIdSchema,
    hook_format: PosterHookFormatSchema,
    hook: PosterHookSchema,
    /** Слово крючка под акцент; пусто — акцента нет. */
    accent: z.string().max(POSTER_HOOK_MAX),
    /** День запланированного слота в зоне бренда — то, что нарисовано в углу. */
    date: PosterDaySchema,
    tag: PosterTagSchema,
    read: PosterReadSchema,
    why: z.string().max(POSTER_WHY_MAX),
  })
  .check((ctx) => {
    const v = ctx.value;
    if (v.accent !== '' && posterAccentIndex(v.hook, v.accent) < 0) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['accent'],
        message: 'poster_accent_not_in_hook',
      });
    }
  });
export type PosterSpec = z.output<typeof PosterSpecSchema>;

/* ─────────────────────────── ответ дизайнера ─────────────────────────── */

/**
 * ОТВЕТ МОДЕЛИ. Порядок полей — порядок мысли, и он записан в схеме намеренно: прочтение
 * поста идёт первым, потому что формат крючка выбирается ПО прочтению, а не прочтение
 * подгоняется под понравившийся крючок. Дату, метку и набор модель не отдаёт — их ставит код.
 *
 * Перечни фонов и форматов здесь открыты: допустимые на ЭТОТ вызов (за вычетом
 * чередования) знает только вызывающий, и сверяет он их сам — схема держит форму.
 */
export const PosterDesignSchema = z.strictObject({
  read: PosterReadSchema,
  hook_format: PosterHookFormatSchema,
  hook: z.string(),
  accent: z.string(),
  background: z.string(),
  why: z.string(),
});
export type PosterDesign = z.output<typeof PosterDesignSchema>;

/**
 * Схема строгого вывода для вызова дизайнера. ПЛОСКАЯ (без `oneOf`, который структурированный
 * вывод Anthropic не принимает — 08-visuals.md, живой отказ 07.09.2026) и собирается НА КАЖДЫЙ
 * ВЫЗОВ: `enum` фонов и форматов — это уже вычтенное чередование, то есть повтор фона
 * соседнего поста модели просто не из чего выбрать. Шкалы — `enum` из пяти чисел, а не
 * `minimum`/`maximum`: перечисление строгий вывод держит наверняка.
 */
export function posterDesignJsonSchema(o: {
  readonly backgrounds: readonly string[];
  readonly formats: readonly PosterHookFormat[];
}): Record<string, unknown> {
  const scale = (description: string): Record<string, unknown> => ({
    type: 'integer',
    enum: [1, 2, 3, 4, 5],
    description,
  });
  return {
    $schema: 'https://json-schema.org/draft/2020-12/schema',
    type: 'object',
    additionalProperties: false,
    required: ['read', 'hook_format', 'hook', 'accent', 'background', 'why'],
    properties: {
      read: {
        type: 'object',
        additionalProperties: false,
        required: ['mood', 'importance', 'shock', 'humor', 'seriousness', 'practical', 'summary'],
        description: 'Прочтение поста. Заполняется первым, до выбора формата крючка.',
        properties: {
          mood: { type: 'string', enum: [...POSTER_MOODS], description: 'Общее настроение.' },
          importance: scale('Насколько это важно читателю канала, 1–5.'),
          shock: scale('Насколько это неожиданно или тревожно, 1–5.'),
          humor: scale('Насколько в поводе есть место улыбке, 1–5.'),
          seriousness: scale('Насколько тема требует сдержанности, 1–5.'),
          practical: scale('Насколько из поста следует действие читателя, 1–5.'),
          summary: {
            type: 'string',
            description: `О чём пост и как он звучит, одной строкой, ${String(POSTER_SUMMARY_MIN)}–${String(POSTER_SUMMARY_MAX)} знаков.`,
          },
        },
      },
      hook_format: {
        type: 'string',
        enum: [...o.formats],
        description: 'Формат крючка, выбранный по прочтению.',
      },
      hook: {
        type: 'string',
        description: `Крючок на картинку, ${String(POSTER_HOOK_MIN)}–${String(POSTER_HOOK_MAX)} знаков.`,
      },
      accent: {
        type: 'string',
        description: 'Одно слово из крючка под акцентный цвет либо пустая строка.',
      },
      background: {
        type: 'string',
        enum: [...o.backgrounds],
        description: 'id фона из перечня в задании.',
      },
      why: {
        type: 'string',
        description: `Почему этот формат и этот фон подходят прочтению, одной строкой, до ${String(POSTER_WHY_MAX)} знаков.`,
      },
    },
  };
}

/* ─────────────────────────── чередование ─────────────────────────── */

/**
 * Место обложки относительно ленты канала: вышла, стоит в слоте, ждёт решения. Роль, а не
 * статус версии: правилу чередования всё равно, `approved` пост или `deferred`, — ему важно,
 * окажется ли он соседом нового поста в ленте.
 */
export const POSTER_HISTORY_ROLES = ['published', 'scheduled', 'review'] as const;

export const PosterHistoryEntrySchema = z.strictObject({
  draft_id: z.string().regex(UUID_V7_RE),
  background: z.string().max(64),
  /**
   * Строка, а не перечисление: в базе лежат спецификации, записанные любой прошлой версией
   * списка форматов, и снятый формат не должен ронять разбор всей истории.
   */
  hook_format: z.string().max(64),
  role: z.enum(POSTER_HISTORY_ROLES),
  /** Когда вышел (`published`), на когда стоит (`scheduled`) или когда создан (`review`). */
  role_at: z.string().regex(ISO_UTC_MS),
  /** Когда создана версия с этой обложкой — по нему считается чередование форматов. */
  created_at: z.string().regex(ISO_UTC_MS),
});
export type PosterHistoryEntry = z.output<typeof PosterHistoryEntrySchema>;

/** Ответ `GET /intake/v1/posters/recent?brand=…`. */
export const PosterHistoryResponseSchema = z.strictObject({
  entries: z.array(PosterHistoryEntrySchema).max(100),
});
export type PosterHistoryResponse = z.output<typeof PosterHistoryResponseSchema>;

/**
 * ЧТО НЕЛЬЗЯ ПОВТОРИТЬ. Задание Ильи: «фон не должен повторяться с предыдущим постом». Но
 * какой пост окажется предыдущим в ленте, при генерации неизвестно: слот выбирают при
 * одобрении, черновики обгоняют друг друга, а бэклог растёт на один пост в неделю (четыре
 * автоматических черновика на три слота). Поэтому «предыдущий» здесь — любой возможный
 * сосед: последний вышедший пост, всё, что стоит в слотах, и всё, что ждёт решения.
 *
 * Свой черновик исключается: перерисовка обложки не соседствует сама с собой.
 */
export function posterExclusions(
  entries: readonly PosterHistoryEntry[],
  selfDraftId: string | null,
): { readonly backgrounds: ReadonlySet<string>; readonly formats: ReadonlySet<string> } {
  const others = entries.filter((e) => e.draft_id !== selfDraftId);
  const backgrounds = new Set<string>();
  const latestPublished = others
    .filter((e) => e.role === 'published')
    .sort((a, b) => b.role_at.localeCompare(a.role_at))[0];
  if (latestPublished !== undefined) backgrounds.add(latestPublished.background);
  for (const e of others) {
    if (e.role !== 'published') backgrounds.add(e.background);
  }
  const formats = new Set(
    [...others]
      .sort((a, b) => b.created_at.localeCompare(a.created_at))
      .slice(0, POSTER_FORMAT_GAP)
      .map((e) => e.hook_format),
  );
  return { backgrounds, formats };
}

/**
 * Фоны, из которых дизайнер выбирает. Полное чередование, пока после него остаётся
 * `POSTER_MIN_BACKGROUND_CHOICE`; иначе — только буква задания: не повторить последний
 * вышедший и самый свежий из созданных. Пустым перечень не бывает, пока в наборе больше
 * одного фона: картинка с повтором фона лучше поста без картинки, а набор из одного фона —
 * это ошибка конфигурации, которую ловит загрузка набора, а не этот выбор.
 */
export function allowedPosterBackgrounds(
  all: readonly string[],
  entries: readonly PosterHistoryEntry[],
  selfDraftId: string | null,
): readonly string[] {
  const { backgrounds } = posterExclusions(entries, selfDraftId);
  const full = all.filter((id) => !backgrounds.has(id));
  if (full.length >= Math.min(POSTER_MIN_BACKGROUND_CHOICE, all.length)) return full;

  const others = entries.filter((e) => e.draft_id !== selfDraftId);
  const letter = new Set<string>();
  const latestPublished = others
    .filter((e) => e.role === 'published')
    .sort((a, b) => b.role_at.localeCompare(a.role_at))[0];
  if (latestPublished !== undefined) letter.add(latestPublished.background);
  const newest = [...others].sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
  if (newest !== undefined) letter.add(newest.background);
  const relaxed = all.filter((id) => !letter.has(id));
  return relaxed.length > 0 ? relaxed : all;
}

/** Форматы крючка за вычетом `POSTER_FORMAT_GAP` последних. */
export function allowedPosterFormats(
  entries: readonly PosterHistoryEntry[],
  selfDraftId: string | null,
): readonly PosterHookFormat[] {
  const { formats } = posterExclusions(entries, selfDraftId);
  return POSTER_HOOK_FORMATS.filter((f) => !formats.has(f));
}

/* ─────────────────────────── дата ─────────────────────────── */

/** Слот расписания бренда в той форме, в какой он лежит в `publishing.slots`. */
export interface PosterScheduleSlot {
  readonly weekday: number;
  readonly time: string;
}

/**
 * ДЕНЬ, КОТОРЫЙ РИСУЕТСЯ В УГЛУ: день ближайшего слота расписания не раньше, чем через
 * `POSTER_SLOT_LEAD_MS` (решение Ильи 2026-10-28, вариант «дата запланированного слота»).
 *
 * Занятость слотов не учитывается, и это не упущение. Писатель не видит расписания (скоупа
 * `schedule:read` у него нет), а «первый свободный» всё равно разошёлся бы с решением
 * человека, который двигает посты как хочет. Расписание же совпадает с привычным ходом:
 * новость понедельника — вторник 12:00, среды — четверг, пятничные новость и дайджест —
 * пятница 17:00. Расхождение ловит проверка при одобрении (`posterDateMismatch`), и лечится
 * оно перерисовкой даты без вызова модели.
 *
 * Слотов нет вовсе — сегодняшний день: картинку без даты не рисуем, а день генерации
 * честнее выдуманного.
 */
export function plannedPosterDay(
  slots: readonly PosterScheduleSlot[],
  tz: string,
  fromMs: number,
): string {
  const DAY = 86_400_000;
  const earliest = fromMs + POSTER_SLOT_LEAD_MS;
  let best: number | null = null;
  // Две недели и один день: самый дальний ближайший слот недельного расписания — через семь
  // суток, а запас в два часа и переход часов могут сдвинуть его ещё на сутки.
  for (let d = 0; d <= 15; d++) {
    const day = dayKeyOf(fromMs + d * DAY, tz);
    const weekday = isoWeekdayOfDayKey(day);
    for (const s of slots) {
      if (s.weekday !== weekday) continue;
      let at: number;
      try {
        at = wallMinuteToMs(`${day}T${s.time}`, tz).ms;
      } catch {
        // Слот в «пропавшем» часе весеннего перевода не существует — так же его пропускает
        // материализация сетки на бэкенде.
        continue;
      }
      if (at >= earliest && (best === null || at < best)) best = at;
    }
  }
  return dayKeyOf(best ?? fromMs, tz);
}

/**
 * Расхождение даты обложки со слотом — `null`, если совпадает. Одна функция на проверку при
 * одобрении (бэкенд) и на подсказку в диалоге (интерфейс): два места, считающие день слота
 * по-своему, однажды разошлись бы на переходе часов.
 */
export function posterDateMismatch(
  posterDay: string,
  slotAtMs: number,
  tz: string,
): { readonly poster_date: string; readonly slot_date: string } | null {
  const slotDay = dayKeyOf(slotAtMs, tz);
  return slotDay === posterDay ? null : { poster_date: posterDay, slot_date: slotDay };
}

const MONTHS_GENITIVE = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
] as const;

/**
 * Подпись даты: «30 октября». Год не пишется: пост в ленте и так несёт время Telegram, а год
 * на каждой обложке — шум. Регистр поднимает вёрстка. Одна функция на картинку и на диалог
 * одобрения: интерфейс говорит «на обложке 30 октября» теми же словами, что нарисованы.
 */
export function posterDateLabel(day: string): string {
  if (!isPosterDay(day)) throw new Error(`posterDateLabel: не календарная дата ${day}`);
  const [, m, d] = day.split('-').map(Number) as [number, number, number];
  return `${String(d)} ${MONTHS_GENITIVE[m - 1] as string}`;
}

/* ─────────────────────────── набор фонов ─────────────────────────── */

/**
 * Поверхность фона — от неё зависит, какими цветами набран текст. Три, как у правил дизайна
 * GeoBoost v3.7 (§6 «Поверхности и свечение»): светлая, тёмная (ink), акцентная (violet).
 */
export const POSTER_SURFACES = ['light', 'ink', 'violet'] as const;
export const PosterSurfaceSchema = z.enum(POSTER_SURFACES);
export type PosterSurface = z.output<typeof PosterSurfaceSchema>;

/**
 * ГЕОМЕТРИЯ, НА КОТОРУЮ ОПИРАЮТСЯ ФОНЫ. Дата и метка стоят в верхней полосе всегда в одном
 * месте, а зона крючка у каждого фона своя (`text_box`) — но не выше полосы и не ниже
 * нижнего поля. Числа общие для вёрстки (`poster-template.ts`) и для проверки набора: фон,
 * чья зона залезла в полосу даты, отбивается при загрузке, а не обнаруживается на картинке.
 */
export const POSTER_PAD_X = 96;
/** Нижняя граница полосы даты и метки. */
export const POSTER_META_BAND_BOTTOM = 200;
export const POSTER_TEXT_BOX_TOP_MIN = 230;
export const POSTER_TEXT_BOX_BOTTOM_MAX = 1000;
/** Три строки нижнего кегля лестницы и воздух вокруг — меньше зона не бывает. */
export const POSTER_TEXT_BOX_MIN_W = 900;
export const POSTER_TEXT_BOX_MIN_H = 420;

const HEX = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'ожидался цвет вида #rrggbb');
const Px = z.int().nonnegative();

export const PosterBackgroundSchema = z.strictObject({
  id: PosterBackgroundIdSchema,
  file: z.string().regex(/^[a-z0-9][a-z0-9-]*\.png$/, 'ожидалось имя PNG-файла набора'),
  /** sha256 байтов PNG: файл сверяется при загрузке набора, как гарнитура (`fonts.ts`). */
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  surface: PosterSurfaceSchema,
  /** Каким настроениям поста фон подходит — по ним дизайнер выбирает. */
  moods: z.array(PosterMoodSchema).min(1),
  /** Что на фоне и как он звучит — строка для дизайнера, не указание ему. */
  description: z.string().min(10).max(200),
  text_box: z.strictObject({ x: Px, y: Px, w: Px, h: Px }),
  colors: z.strictObject({ hook: HEX, accent: HEX, meta: HEX }),
});
export type PosterBackground = z.output<typeof PosterBackgroundSchema>;

export const PosterManifestSchema = z
  .strictObject({
    set: PosterSetSchema,
    version: z.int().min(1),
    backgrounds: z.array(PosterBackgroundSchema).min(POSTER_MIN_USABLE_BACKGROUNDS).max(30),
  })
  .check((ctx) => {
    const v = ctx.value;
    const push = (path: (string | number)[], message: string): void => {
      ctx.issues.push({ code: 'custom', input: v, path, message });
    };
    const ids = new Set<string>();
    const files = new Set<string>();
    v.backgrounds.forEach((b, i) => {
      if (ids.has(b.id)) push(['backgrounds', i, 'id'], 'poster_background_id_duplicate');
      if (files.has(b.file)) push(['backgrounds', i, 'file'], 'poster_background_file_duplicate');
      ids.add(b.id);
      files.add(b.file);
      const t = b.text_box;
      if (
        t.x < POSTER_PAD_X ||
        t.x + t.w > VISUAL_WIDTH - POSTER_PAD_X ||
        t.y < POSTER_TEXT_BOX_TOP_MIN ||
        t.y + t.h > POSTER_TEXT_BOX_BOTTOM_MAX
      ) {
        push(['backgrounds', i, 'text_box'], 'poster_text_box_out_of_bounds');
      }
      if (t.w < POSTER_TEXT_BOX_MIN_W || t.h < POSTER_TEXT_BOX_MIN_H) {
        push(['backgrounds', i, 'text_box'], 'poster_text_box_too_small');
      }
    });
  });
export type PosterManifest = z.output<typeof PosterManifestSchema>;

/** Секция `visual.poster` конфига бренда (`brand-config.ts`). */
export const PosterBrandSchema = z.strictObject({
  /** `@username` боевого канала — подпись в углу картинки, не привязка канала. */
  tag: PosterTagSchema,
  /** Набор фонов: каталог `assets/posters/<set>/` с `manifest.json`. */
  set: PosterSetSchema,
  /**
   * Поверхности фонов, которыми бренд рисует; нет поля — все фоны набора. Набор — библиотека,
   * выбор из неё — дело бренда: в новостях GeoBoost только тёмные (`ink`, решение Ильи
   * 2026-10-29), светлые и фиолетовые лежат в наборе до своей рубрики.
   */
  surfaces: z
    .array(PosterSurfaceSchema)
    .min(1)
    .refine((s) => new Set(s).size === s.length, 'поверхность названа дважды')
    .optional(),
});
export type PosterBrand = z.output<typeof PosterBrandSchema>;

/* ─────────────────────────── причина в версии ─────────────────────────── */

/**
 * Приставка `visual_reason`, которой версию помечает ОБЛОЖКА, а не вердикт инфографики. Тот
 * же приём, что `MECHANISM_REASON_PREFIX`: у строки два конца. Пишет её сервис генерации, а
 * читает доля картинок `v_ops_visual_share` (миграция 0027), которая обязана считать ТОЛЬКО
 * вердикты инфографики: обложка есть у каждого поста бренда по построению, и с ней доля
 * стояла бы на 100% и тревожила бы всегда, то есть не значила бы ничего.
 */
export const POSTER_REASON_PREFIX = 'обложка:';

/**
 * Причина версии от обложки. Режется по `VISUAL_REASON_MAX`: в неё попадает текст отказа
 * провайдера или intake, а длина у них не ограничена ничем, — и причина длиннее потолка
 * означала бы 400 на теле версии, то есть пост, потерянный из-за строки пояснения.
 */
export function posterReason(text: string): string {
  const full = `${POSTER_REASON_PREFIX} ${text}`;
  const cps = [...full];
  return cps.length <= VISUAL_REASON_MAX
    ? full
    : `${cps.slice(0, VISUAL_REASON_MAX - 1).join('')}…`;
}

/**
 * Причина версии, которую переделка текста выпускает после картинки, УБРАННОЙ человеком
 * (`picture_removed` вида черновика): обложка не рисуется, полоса говорит почему и предлагает
 * нарисовать её кнопкой. Приставка обложки — чтобы доля картинок (0027) её не считала.
 */
export const POSTER_PICTURE_REMOVED_REASON = posterReason(
  'картинку убрали правкой — переделка текста её не возвращает',
);

/**
 * Причина версии, у которой картинка есть, но не обложка (снимок человека, принятая
 * инфографика): переделка текста её наследует и обложку не рисует. Вердикт `none` с этой
 * причиной держит полосу визуала с кнопками и не прячет висящее предложение инфографики.
 */
export const POSTER_OTHER_PICTURE_REASON = posterReason(
  'картинка версии — не обложка, переделка текста её оставила',
);

/* ─────────────────────────── просьбы человека ─────────────────────────── */

/**
 * ПРИСТАВКИ ПРОСЬБЫ ПЕРЕДЕЛКИ. Перерисовка обложки и перерисовка даты едут той же переделкой
 * `targets: ['visual']`, что и просьба нарисовать инфографику, — и отличаются строкой
 * просьбы. Это тот же приём, что `VISUAL_AUTO_PROMPT` (`auto:`): `rework_request` знает
 * только просьбу и цели, и заводить ради обложки третью колонку значило бы миграцию на
 * таблице, которую она не меняет по смыслу.
 *
 *   `poster: <комментарий>` — перерисовать обложку, комментарий человека — указание
 *                             дизайнеру (может быть пустым);
 *   `redate: YYYY-MM-DD`    — перерисовать только дату, без вызова модели.
 */
export const POSTER_PROMPT_PREFIX = 'poster:';
export const POSTER_REDATE_PREFIX = 'redate:';

export type PosterPrompt =
  | { readonly kind: 'redraw'; readonly comment: string }
  | { readonly kind: 'redate'; readonly day: string }
  | { readonly kind: 'invalid'; readonly reason: string };

/** `null` — просьба не про обложку (инфографика или текст). */
export function parsePosterPrompt(prompt: string): PosterPrompt | null {
  const p = prompt.trim();
  if (p.startsWith(POSTER_REDATE_PREFIX)) {
    const day = p.slice(POSTER_REDATE_PREFIX.length).trim();
    return isPosterDay(day)
      ? { kind: 'redate', day }
      : { kind: 'invalid', reason: `дата перерисовки не календарная: ${JSON.stringify(day)}` };
  }
  if (p.startsWith(POSTER_PROMPT_PREFIX)) {
    return { kind: 'redraw', comment: p.slice(POSTER_PROMPT_PREFIX.length).trim() };
  }
  return null;
}

export function posterRedatePrompt(day: string): string {
  return `${POSTER_REDATE_PREFIX} ${day}`;
}

export function posterRedrawPrompt(comment: string): string {
  const c = comment.trim();
  return c === '' ? POSTER_PROMPT_PREFIX : `${POSTER_PROMPT_PREFIX} ${c}`;
}
